import { useEffect, useMemo, useRef, useState } from 'react'
import type { FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { CircleCheck, CircleAlert, ExternalLink, Loader2 } from 'lucide-react'
import { Button } from '@/src/renderer/components/ui/Button'
import { PasswordInput } from '@/src/renderer/components/ui/PasswordInput'
import { getPlatformAPI } from '@/src/renderer/lib/platform'
import {
  verifyTodoistToken,
  fetchTodoistSnapshot,
  fetchTodoistCompleted,
  fetchTodoistSections,
  TODOIST_TOKEN_SETTINGS_URL,
  type CompletedOptions,
  type CompletedProgress,
  type CompletedResult,
  type SectionsResult,
  type SnapshotResult,
  type TodoistFailure,
  type TodoistUser,
  type VerifyResult,
} from '@/src/renderer/lib/import/todoist/client'
import type { TodoistSnapshot } from '@/src/renderer/lib/import/todoist/types'
import { buildImportPreview } from '@/src/renderer/lib/import/todoist/preview'
import { chooseAll, chooseLifeSpace, type LifeSpaceChoices } from '@/src/renderer/lib/import/todoist/lifeSpaces'
import { planTodoistImport } from '@/src/renderer/lib/import/todoist/plan'
import { loadProjectLayouts } from '@/src/renderer/lib/import/todoist/layouts'
import { runTodoistImport, type ImportResult } from '@/src/renderer/lib/import/todoist/run'
import { useDataSource } from '@/src/renderer/lib/dataSource'
import { SettingsSection, SettingsSubpage } from '../../primitives'
import { ImportPreview } from './ImportPreview'
import './TodoistImportView.scss'

type Check =
  | { state: 'idle' }
  | { state: 'checking' }
  | { state: 'connected'; user: TodoistUser }
  | { state: 'failed'; reason: TodoistFailure }

type Fetch =
  | { state: 'idle' }
  | { state: 'loading' }
  | { state: 'loaded'; snapshot: TodoistSnapshot }
  | { state: 'failed'; reason: TodoistFailure }

/** Der Verlauf (T04) — getrennt vom Aktiven, weil er dauern und stocken kann. */
type Completed =
  | { state: 'idle' }
  | { state: 'loading'; reachedBack: Date | null }
  | { state: 'failed'; reason: TodoistFailure; progress: CompletedProgress }
  | { state: 'loaded'; count: number }

/** Das Anlegen (T07). */
type Run =
  | { state: 'idle' }
  | { state: 'running'; done: number; total: number }
  | { state: 'done'; result: ImportResult }
  | { state: 'failed' }

interface TodoistImportViewProps {
  /** Zurück zur Übersicht „Import & Export“. */
  onBack: () => void
  /** Austauschbar für Story und Test; sonst der echte Abruf bei Todoist. */
  verify?: (token: string) => Promise<VerifyResult>
  /** Wie `verify`: der volle Sync (T03). */
  fetchSnapshot?: (token: string) => Promise<SnapshotResult>
  /** Wie `verify`: alle erledigten Aufgaben (T04). */
  fetchCompleted?: (token: string, options: CompletedOptions) => Promise<CompletedResult>
  /** Wie `verify`: Abschnitte einzeln, für archivierte (#193). */
  fetchSections?: (token: string, ids: string[]) => Promise<SectionsResult>
}

/**
 * **Import & Export › Todoist** — Todoist-Schlüssel eingeben und prüfen (T02, #167), Vorschau, Import.
 * Eine Unterseite der Übersicht „Import & Export“ (#158, Entscheidung 13); solange der Import läuft,
 * sind Zurück und Brotkrumen gesperrt.
 *
 * Der Schlüssel lebt nur im State dieser Seite: kein localStorage, keine
 * Datenbank, kein Sync (docs/concept/todoist-import.md). Schließt das
 * Einstellungsfenster, ist er weg.
 */
export function TodoistImportView({
  onBack,
  verify = verifyTodoistToken,
  fetchSnapshot = fetchTodoistSnapshot,
  fetchCompleted = fetchTodoistCompleted,
  fetchSections = fetchTodoistSections,
}: TodoistImportViewProps) {
  const { t, i18n } = useTranslation()
  const [token, setToken] = useState('')
  const [check, setCheck] = useState<Check>({ state: 'idle' })
  const [fetched, setFetched] = useState<Fetch>({ state: 'idle' })
  const [completed, setCompleted] = useState<Completed>({ state: 'idle' })
  const [lifeSpaces, setLifeSpaces] = useState<LifeSpaceChoices>({})
  const [run, setRun] = useState<Run>({ state: 'idle' })
  const dataSource = useDataSource()
  const tags = dataSource.useTags()
  // Eine Antwort, die nach einer neuen Eingabe eintrifft, gehört zu einem alten Schlüssel.
  const attempt = useRef(0)
  // Hält den laufenden Verlaufs-Abruf an, wenn der Schlüssel wechselt oder die Seite geht.
  const abort = useRef<AbortController | null>(null)

  useEffect(() => () => abort.current?.abort(), [])

  const onTokenChange = (value: string) => {
    setToken(value)
    attempt.current++
    abort.current?.abort()
    setCheck({ state: 'idle' })
    setFetched({ state: 'idle' })
    setCompleted({ state: 'idle' })
    setLifeSpaces({})
    setRun({ state: 'idle' })
  }

  /**
   * Holt den Verlauf — von vorn oder, mit `resume`, dort weiter, wo es hing.
   *
   * Danach die **archivierten Abschnitte** (#193): Der Schnappschuss kennt sie nicht, erledigte
   * Aufgaben verweisen aber auf sie. Geholt wird nur, worauf verwiesen wird (`known` sind die
   * Abschnitte des Schnappschusses). Scheitert das, gilt der Verlauf als gescheitert — mit einem
   * Stand ohne offene Fenster, „Erneut versuchen“ holt dann nur die Abschnitte.
   */
  const loadCompleted = async (mine: number, since: string | null, known: readonly { id: string }[], resume?: CompletedProgress) => {
    const controller = new AbortController()
    abort.current = controller
    setCompleted({ state: 'loading', reachedBack: null })
    const result = await fetchCompleted(token, {
      since: since ? new Date(since) : null,
      resume,
      signal: controller.signal,
      onProgress: (reachedBack) => {
        if (mine === attempt.current) setCompleted({ state: 'loading', reachedBack })
      },
    })
    if (mine !== attempt.current || result.ok === false && result.reason === 'aborted') return
    if (!result.ok) {
      setCompleted({ state: 'failed', reason: result.reason, progress: result.progress })
      return
    }
    const knownIds = new Set(known.map(section => section.id))
    const missing = [...new Set(result.items.flatMap(item => (item.section_id && !knownIds.has(item.section_id) ? [item.section_id] : [])))]
    const archived = missing.length > 0 ? await fetchSections(token, missing) : { ok: true as const, sections: [], skipped: 0 }
    if (mine !== attempt.current || archived.ok === false && archived.reason === 'aborted') return
    if (!archived.ok) {
      setCompleted({
        state: 'failed',
        reason: archived.reason,
        progress: { items: result.items, skipped: result.skipped, windows: [], cursor: null },
      })
      return
    }
    // Erst jetzt in den Schnappschuss: Ein halber Verlauf geht nie in den Import.
    setFetched(prev => prev.state === 'loaded'
      ? {
          ...prev,
          snapshot: {
            ...prev.snapshot,
            sections: [...prev.snapshot.sections, ...archived.sections.filter(s => !prev.snapshot.sections.some(k => k.id === s.id))],
            completedItems: result.items,
            skipped: prev.snapshot.skipped + result.skipped + archived.skipped,
          },
        }
      : prev)
    setCompleted({ state: 'loaded', count: result.items.length })
  }

  // Neu rechnen nur, wenn der Schnappschuss wechselt (Aktives da, dann Verlauf dazu).
  const snapshot = fetched.state === 'loaded' ? fetched.snapshot : null
  const preview = useMemo(() => (snapshot ? buildImportPreview(snapshot) : null), [snapshot])

  /**
   * Plant gegen das, was schon da ist (`importedFrom`), und legt an. Scheitert
   * es mittendrin, trägt das Angelegte seine Herkunft — ein neuer Klick macht weiter.
   */
  const onImport = async () => {
    if (!snapshot || run.state === 'running') return
    setRun({ state: 'running', done: 0, total: 0 })
    try {
      const imported = await dataSource.findImported('todoist')
      const plan = planTodoistImport({
        snapshot,
        lifeSpaces,
        imported,
        // Neues in schon übernommenen Projekten kommt in deren Lücken (#317).
        layouts: await loadProjectLayouts(dataSource, imported),
        tags,
        texts: {
          attachment: t('preferences.import.run.attachment'),
          recurrence: t('preferences.import.run.recurrenceNote'),
        },
      })
      const result = await runTodoistImport(plan, dataSource, (done, total) => setRun({ state: 'running', done, total }))
      setRun({ state: 'done', result })
    } catch (err) {
      console.error('[Import] Todoist import failed:', err)
      setRun({ state: 'failed' })
    }
  }

  const monthYear = (date: Date) => date.toLocaleDateString(i18n?.language, { month: '2-digit', year: 'numeric' })

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault()
    if (!token.trim() || check.state === 'checking') return
    const mine = ++attempt.current
    setCheck({ state: 'checking' })
    const result = await verify(token)
    if (mine !== attempt.current) return
    if (!result.ok) {
      setCheck({ state: 'failed', reason: result.reason })
      return
    }
    setCheck({ state: 'connected', user: result.user })

    // Gleich weiter: Die Vorschau (T05) braucht den Schnappschuss ohnehin.
    setFetched({ state: 'loading' })
    const snapshot = await fetchSnapshot(token)
    if (mine !== attempt.current) return
    if (!snapshot.ok) {
      setFetched({ state: 'failed', reason: snapshot.reason })
      return
    }
    setFetched({ state: 'loaded', snapshot: snapshot.snapshot })
    await loadCompleted(mine, result.user.joinedAt, snapshot.snapshot.sections)
  }

  const onResume = () => {
    if (completed.state !== 'failed' || check.state !== 'connected' || fetched.state !== 'loaded') return
    void loadCompleted(attempt.current, check.user.joinedAt, fetched.snapshot.sections, completed.progress)
  }

  const openTodoistSettings = () => {
    getPlatformAPI()
      .openExternal(TODOIST_TOKEN_SETTINGS_URL)
      .catch((err) => console.error('[Import] Failed to open Todoist settings:', err))
  }

  return (
    <SettingsSubpage
      pageTitle={t('settings.subNav.import')}
      title={t('preferences.import.todoist.title')}
      onBack={onBack}
      locked={run.state === 'running'}
    >
      <SettingsSection title={t('preferences.import.todoist.connectTitle')} variant="card">
        <div className="import-page__guide">
          <p className="import-page__intro">{t('preferences.import.todoist.intro')}</p>

          <ol className="import-page__steps">
            <li>
              {t('preferences.import.todoist.step1')}{' '}
              <button type="button" className="import-page__link" onClick={openTodoistSettings}>
                {t('preferences.import.todoist.openSettings')}
                <ExternalLink size={12} strokeWidth={1.8} aria-hidden="true" />
              </button>
            </li>
            <li>{t('preferences.import.todoist.step2')}</li>
            <li>{t('preferences.import.todoist.step3')}</li>
          </ol>
        </div>

        {/* Eingabe und Meldungen in einer Fläche — eine leere Meldung soll keine leere Fläche zeigen. */}
        <div className="import-page__connect">
          <form className="import-page__form" onSubmit={onSubmit}>
            <label className="import-page__label" htmlFor="import-todoist-token">
              {t('preferences.import.todoist.tokenLabel')}
            </label>
            <div className="import-page__row">
              <PasswordInput
                id="import-todoist-token"
                className="import-page__input"
                value={token}
                onChange={(e) => onTokenChange(e.target.value)}
                placeholder={t('preferences.import.todoist.tokenPlaceholder')}
                autoComplete="off"
                spellCheck={false}
                error={check.state === 'failed' && check.reason === 'invalid'}
              />
              <Button type="submit" variant="primary" disabled={!token.trim() || check.state === 'checking'}>
                {check.state === 'checking' ? t('preferences.import.todoist.checking') : t('preferences.import.todoist.check')}
              </Button>
            </div>
            <p className="import-page__hint">{t('preferences.import.todoist.notStored')}</p>
          </form>

          <div className="import-page__status" role="status" aria-live="polite">
            {check.state === 'connected' && (
              <p className="import-page__result import-page__result--connected">
                <CircleCheck size={16} aria-hidden="true" />
                {t('preferences.import.todoist.connected', {
                  name: check.user.fullName || check.user.email,
                  email: check.user.email,
                })}
              </p>
            )}
            {fetched.state === 'loading' && (
              <p className="import-page__result import-page__result--loading">
                <Loader2 size={16} className="import-page__spinner" aria-hidden="true" />
                {t('preferences.import.todoist.loading')}
              </p>
            )}
            {completed.state === 'loading' && (
              <p className="import-page__result import-page__result--loading">
                <Loader2 size={16} className="import-page__spinner" aria-hidden="true" />
                {completed.reachedBack
                  ? t('preferences.import.todoist.loadingCompletedUntil', { date: monthYear(completed.reachedBack) })
                  : t('preferences.import.todoist.loadingCompleted')}
              </p>
            )}
            {completed.state === 'failed' && (
              <div className="import-page__result import-page__result--failed">
                <CircleAlert size={16} aria-hidden="true" />
                <span>{t(`preferences.import.todoist.error.${completed.reason}`)}</span>
                <Button variant="secondary" onClick={onResume}>
                  {completed.progress.windows[0]
                    ? t('preferences.import.todoist.resume', { date: monthYear(completed.progress.windows[0].until) })
                    : t('preferences.import.todoist.resumePlain')}
                </Button>
              </div>
            )}
            {fetched.state === 'failed' && (
              <p className="import-page__result import-page__result--failed">
                <CircleAlert size={16} aria-hidden="true" />
                {t(`preferences.import.todoist.error.${fetched.reason}`)}
              </p>
            )}
            {check.state === 'failed' && (
              <p className="import-page__result import-page__result--failed">
                <CircleAlert size={16} aria-hidden="true" />
                {t(`preferences.import.todoist.error.${check.reason}`)}
              </p>
            )}
          </div>
        </div>
      </SettingsSection>

      {preview && (
        <SettingsSection title={t('preferences.import.preview.title')} variant="card">
          <ImportPreview
            preview={preview}
            completedLoading={completed.state === 'loading'}
            lifeSpaces={lifeSpaces}
            onLifeSpaceChange={(projectId, value) =>
              setLifeSpaces(prev => chooseLifeSpace(prev, snapshot?.projects ?? [], projectId, value))}
            onAllLifeSpaces={value => setLifeSpaces(chooseAll(snapshot?.projects ?? [], value))}
          />
          <div className="import-page__run">
            <Button
              variant="primary"
              disabled={completed.state !== 'loaded' || run.state === 'running'}
              // Solange der Knopf auf den Verlauf oder den Import wartet, dreht er sich —
              // nicht bei einem Fehler, da wartet er auf nichts.
              icon={completed.state === 'loading' || run.state === 'running'
                ? <Loader2 size={16} className="import-page__spinner" aria-hidden="true" />
                : undefined}
              onClick={() => void onImport()}
            >
              {run.state === 'running' ? t('preferences.import.run.running') : t('preferences.import.run.start')}
            </Button>
            <div className="import-page__status" role="status" aria-live="polite">
              {run.state === 'running' && (
                <>
                  <p className="import-page__result import-page__result--loading">
                    <Loader2 size={16} className="import-page__spinner" aria-hidden="true" />
                    {run.total > 0
                      ? t('preferences.import.run.progress', { done: run.done, total: run.total })
                      : t('preferences.import.run.preparing')}
                  </p>
                  <progress className="import-page__bar" max={Math.max(run.total, 1)} value={run.done} />
                </>
              )}
              {run.state === 'done' && (
                <p className="import-page__result import-page__result--connected">
                  <CircleCheck size={16} aria-hidden="true" />
                  {run.result.projects + run.result.sections + run.result.tags + run.result.tasks + run.result.completed === 0
                    ? t('preferences.import.run.nothingNew')
                    : t('preferences.import.run.done', {
                        projects: run.result.projects,
                        tasks: run.result.tasks,
                        completed: run.result.completed,
                        subtasks: run.result.subtasks,
                        tags: run.result.tags,
                      })}
                  {run.result.skipped > 0 && ` ${t('preferences.import.run.skipped', { count: run.result.skipped })}`}
                </p>
              )}
              {run.state === 'failed' && (
                <p className="import-page__result import-page__result--failed">
                  <CircleAlert size={16} aria-hidden="true" />
                  {t('preferences.import.run.failed')}
                </p>
              )}
            </div>
          </div>
        </SettingsSection>
      )}
    </SettingsSubpage>
  )
}
